/**
 * What features lend the tour engine (plan
 * guide-can-walk-a-person-through-a-task-highlight, item tour-engine;
 * docs/architecture/guide-anchors.md "The tour engine"). The platform never
 * imports a feature, so a feature registers here:
 *
 *   - its STATE READERS: the feature client's own functions that a declared
 *     state read names by its contract (shared/features/<feature>.anchors.ts),
 *     e.g. msgraph's getEmailChannel and getAgentAreas. They run in the
 *     browser with the person's own sign-ins, exactly as the page calls them;
 *     the engine never makes a raw HTTP call. Registered once, from the
 *     feature's client module.
 *
 *   - its REVEALS (useGuideReveal): how to show one of its anchors when the
 *     page has it but keeps it folded away (a finished Email row collapses
 *     inside the setup wizard). A reveal changes only the page's own view
 *     state, like opening the row's "Change"; it never clicks, types or
 *     submits. Registered while the component that can reveal is mounted.
 */
import { useEffect } from 'react'

const readers = new Map()
const readerListeners = new Set()
const reveals = new Map()

/**
 * Register a feature client's functions as the state readers for its declared
 * reads: `registerGuideStateReaders('msgraph', { getEmailChannel, getAgentAreas })`.
 * Each is called with no arguments and answers what the client answers (an
 * axios response; the read's test gets its `data`).
 */
export function registerGuideStateReaders(feature, functions) {
  for (const [contract, fn] of Object.entries(functions)) {
    if (typeof fn === 'function') readers.set(`${feature}:${contract}`, fn)
  }
  for (const listener of [...readerListeners]) listener()
}

/** The registered reader for a feature's contract, or null until the feature's client has loaded. */
export function guideStateReader(feature, contract) {
  return readers.get(`${feature}:${contract}`) || null
}

/** Hear when readers are registered (a feature's client loads after the tour started). */
export function subscribeGuideStateReaders(listener) {
  readerListeners.add(listener)
  return () => readerListeners.delete(listener)
}

/**
 * Lend the engine a way to reveal some of this component's anchors while it
 * is mounted: `useGuideReveal({ 'msgraph.email.mailbox': () => openRow('mailbox') })`.
 * Each function receives the step's anchor key (null for an unkeyed anchor).
 * Pass a stable object (useMemo): it is registered once per change.
 */
export function useGuideReveal(map) {
  useEffect(() => {
    const entries = Object.entries(map || {}).filter(([, fn]) => typeof fn === 'function')
    for (const [anchor, fn] of entries) {
      if (!reveals.has(anchor)) reveals.set(anchor, new Set())
      reveals.get(anchor).add(fn)
    }
    return () => {
      for (const [anchor, fn] of entries) {
        reveals.get(anchor)?.delete(fn)
        if (reveals.get(anchor)?.size === 0) reveals.delete(anchor)
      }
    }
  }, [map])
}

/** Ask the mounted owner of an anchor to reveal it. True when one did. */
export function revealGuideAnchor(anchor, key = null) {
  const owners = reveals.get(anchor)
  if (!owners?.size) return false
  for (const reveal of [...owners]) reveal(key)
  return true
}

/** Test seam: forget every registration. */
export function resetGuideRegistryForTests() {
  readers.clear()
  readerListeners.clear()
  reveals.clear()
}
